import { AfterViewInit, Directive, DoCheck, Input, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { HasPermission } from '../directives/has-permission/has-permission.service';
import { UrlStateService } from './url-state.service';

export interface PanelUrlConfig {
  create?: () => void;
  edit?: (id: string) => void;
  close: () => void;
  state: () => { open: boolean; id?: string | number | null };
  entity: string;
  createAction?: string;
  editAction?: string;
  scope?: string;
}

@Directive({ selector: '[ssPanelUrlState]', standalone: false })
export class PanelUrlStateDirective implements AfterViewInit, DoCheck, OnDestroy {
  @Input('ssPanelUrlState') config!: PanelUrlConfig;
  private subscription?: Subscription;
  private initialized = false;
  private wasOpen = false;
  private requested = '';
  private destroyed = false;

  constructor(private router: Router, private urlState: UrlStateService) { }

  ngAfterViewInit(): void {
    Promise.resolve().then(() => {
      if (this.destroyed) return;
      this.initialized = true;
      this.restore();
      this.subscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => this.restore());
    });
  }

  ngDoCheck(): void {
    if (!this.initialized || !this.config) return;
    const state = this.config.state();
    if (state.open === this.wasOpen) return;
    this.wasOpen = state.open;
    if (state.open) {
      const editing = state.id !== null && state.id !== undefined && state.id !== '';
      this.requested = editing ? `edit:${state.id}` : 'new';
      this.urlState.patch({ novo: editing ? null : 'true', editar: editing ? String(state.id) : null, painel: this.config.scope || null }, true);
    } else {
      this.requested = '';
      this.clear();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscription?.unsubscribe();
  }

  private restore(): void {
    const values = this.urlState.read([
      { key: 'novo', type: 'enum', options: ['true', '1'] },
      { key: 'editar', type: 'id' },
      { key: 'painel', type: 'id' }
    ]);
    const rawNew = this.urlState.params['novo'];
    const isNew = values['novo'] !== undefined || rawNew === '';
    const matches = (values['painel'] || '') === (this.config.scope || '');
    const action = matches ? values['editar'] ? `edit:${values['editar']}` : isNew ? 'new' : '' : '';
    if (action === this.requested) return;
    this.requested = action;
    if (!action) {
      if (this.wasOpen) this.config.close();
      return;
    }
    const editing = action.startsWith('edit:');
    const permission = editing ? this.config.editAction || 'update' : this.config.createAction || 'create';
    const callback = editing ? this.config.edit : this.config.create;
    if (!callback || !HasPermission.check(this.config.entity, permission)) {
      this.requested = '';
      this.clear();
      return;
    }
    if (editing) this.config.edit?.(String(values['editar']));
    else this.config.create?.();
  }

  private clear(): void {
    const scope = this.urlState.read([{ key: 'painel', type: 'id' }])['painel'] || '';
    if (scope === (this.config.scope || '')) this.urlState.patch({ novo: null, editar: null, painel: null });
  }
}
